import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useNotifications } from '../../../../../providers';
import { AssetActions, VegaChart } from '../../molecules';
import { AssetError } from '../AssetError';

import { Card, NewButton } from '../../../../../components';

import { questionShape, sourceShape } from '../../../../../propsShape';

const AssetChartViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  padding: 0 16px;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const AssetChartView = ({
  question,
  onDeleteQuestion,
  onRetryQuestion = null,
  emptyComponent = null,
  answerSources = [],
  selectSpaceDropdown = null
}) => {
  const { addNotification } = useNotifications();
  const vegaRef = useRef(null);

  const onExportChart = useCallback(async () => {
    const url = await vegaRef.current.view.toImageURL('png');

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('target', '_blank');
    link.setAttribute('download', `${question.exportFileName} - chart.png`);
    link.dispatchEvent(new MouseEvent('click'));

    addNotification({
      type: 'info',
      icon: 'chart-mode',
      message: `Chart was saved as "${question.exportFileName} - chart.png"`
    });
  }, [vegaRef.current, question.exportFileName]);

  const chartFollowUp = question.currentChartFollowUp || question.lastChartFollowUp || question;

  if (chartFollowUp.answer?.error) {
    return (
      <AssetChartViewContainer>
        <AssetError
          questionBody={chartFollowUp.body}
          answerBody={chartFollowUp.answer.body}
          actions={
            <NewButton tertiary onClick={() => onDeleteQuestion(chartFollowUp.id)}>
              Undo
            </NewButton>
          }
          onRetryQuestion={() => onRetryQuestion(chartFollowUp.id)}
        />
      </AssetChartViewContainer>
    );
  }
  if (chartFollowUp?.answer?.vegaSpec && chartFollowUp.status === null) {
    return (
      <AssetChartViewContainer>
        <>
          <ChartHeader>
            <AssetActions onDownload={onExportChart} sources={answerSources} />
            {selectSpaceDropdown}
          </ChartHeader>
          <Card>
            <VegaChart ref={vegaRef} spec={chartFollowUp.answer.vegaSpec} />
          </Card>
        </>
      </AssetChartViewContainer>
    );
  }

  return emptyComponent;
};

AssetChartView.propTypes = {
  question: questionShape.isRequired,
  emptyComponent: PropTypes.node,
  onDeleteQuestion: PropTypes.func.isRequired,
  onRetryQuestion: PropTypes.func,
  answerSources: PropTypes.arrayOf(sourceShape).isRequired,
  selectSpaceDropdown: PropTypes.node
};

export { AssetChartView };
